import { Select } from 'antd';
import { useQuery } from '@tanstack/react-query';
import type { FocusEventHandler } from 'react';
import { locationService, type Ward } from '@/services/location.service';

interface Props {
  cityId: string;
  value?: string;
  onChange: (wardId: string) => void;
  onChangeWithLabel?: (wardId: string, name: string) => void;
  placeholder?: string;
  status?: 'error' | 'warning';
  allowClear?: boolean;
  // Forwarded so FloatingField can toggle the floating-label state.
  onFocus?: FocusEventHandler<HTMLElement>;
  onBlur?: FocusEventHandler<HTMLElement>;
}

export function WardAsyncSelect({
  cityId,
  value,
  onChange,
  onChangeWithLabel,
  placeholder = 'Chọn phường/xã',
  status,
  allowClear = true,
  onFocus,
  onBlur,
}: Props) {
  const { data = [], isLoading, isError } = useQuery({
    queryKey: ['locations', 'wards', cityId],
    queryFn: () => locationService.listWardsByCity(cityId),
    enabled: !!cityId,
    staleTime: 30 * 60 * 1000,
  });

  const options = data.map((w: Ward) => ({ value: w._id, label: w.name }));

  return (
    <Select
      style={{ width: '100%' }}
      size="small"
      showSearch
      optionFilterProp="label"
      placeholder={isError ? 'Lỗi tải danh sách...' : (cityId ? placeholder : 'Chọn tỉnh/thành trước')}
      value={value || undefined}
      loading={isLoading}
      options={options}
      disabled={!cityId || isError}
      allowClear={allowClear}
      status={isError ? 'error' : status}
      onFocus={onFocus}
      onBlur={onBlur}
      onChange={(v: string) => {
        onChange(v ?? '');
        if (onChangeWithLabel) {
          const found = options.find((o) => o.value === v);
          onChangeWithLabel(v ?? '', found?.label ?? '');
        }
      }}
    />
  );
}
